import { css } from '@emotion/css'
import { Group, Select, Text } from '@mantine/core'
import type { TPermission } from '@semios/app-platform-value-type-definitions'
import { PanelDetailsContext } from 'App/Map/PanelDetails/PanelDetails'
import type { TValuesTimeseriesBlocks } from 'App/Map/types'
import { ExternalVendorIcons } from 'components/ExternalVendorIcons/ExternalVendorIcons'
import { translate } from 'i18n/i18n'
import { useContext, useMemo } from 'react'
import { colors } from 'settings/colors'
import { hubConnectorSettings } from 'settings/hubConnectorSettings'
import { SharedSettings } from 'settings/SharedSettings'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import type { TPoint } from 'stores/fieldAssetStore/helperTypes'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { userDetailsStore } from 'stores/userDetailsStore/userDetailsStore'
import { alphabeticalSort } from 'utils/alphabeticalSort'
import { filterFieldAssetsByValueTypes } from 'utils/filterFieldAssetsByValueTypes'
import { getExternalVendorIconItems } from 'utils/getExternalVendorIconItems'
import { mantineSelectSearchFilterProp } from 'utils/mantineSelectSearchFilterProp'
import { setSelectedFieldAsset } from 'utils/setSelectedFieldAsset/setSelectedFieldAsset'
import { sortByKey } from 'utils/sortByKey'
import { useSelectClickToggle } from 'utils/useSelectClickToggle'

type SelectDataItem = {
  value: string
  label: string
  associatedWeatherStationName: string | undefined
  associatedWeatherStationExternalVendorNames: TPoint['externalVendorNames']
}

type SelectDataGroup = {
  group: string
  items: SelectDataItem[]
}

export type TDropdownSelectorBlockProps = {
  propertyPermissionsToCheck?: TPermission[]
  valuesTimeseriesToFilterOn?: TValuesTimeseriesBlocks[]
  showAssociatedWeatherStationName?: boolean
}

export const DropdownSelectorBlock = ({
  valuesTimeseriesToFilterOn,
  showAssociatedWeatherStationName,
  propertyPermissionsToCheck,
}: TDropdownSelectorBlockProps) => {
  const { selectedBlock } = selectedFieldAssetsStore.useSelector((s) => ({
    selectedBlock: s.block,
  }))

  const { containerWidth } = useContext(PanelDetailsContext)
  const selectClickToggle = useSelectClickToggle()

  const { properties } = fieldAssetStore.useSelector((s) => ({
    properties: s?.properties ?? [],
  }))

  const permissions = userDetailsStore.useSelector((s) => s.permissions)

  const data = useMemo<SelectDataGroup[]>(() => {
    return Object.values(properties ?? {})
      .sort(sortByKey('propertyName'))
      .flatMap(({ blocks, points, propertyName, propertyId }) => {
        if (!blocks) return []

        if (propertyPermissionsToCheck) {
          const hasPermission = propertyPermissionsToCheck.some(
            (permission) => permissions[permission]?.[propertyId],
          )

          if (!hasPermission) return []
        }

        let filteredBlocks = Object.values(blocks)

        if (valuesTimeseriesToFilterOn) {
          filteredBlocks = filterFieldAssetsByValueTypes({
            fieldAssets: Object.values(blocks),
            valuesTimeseries: valuesTimeseriesToFilterOn,
          })
        }

        const sortedBlocks = filteredBlocks.sort((a, b) =>
          alphabeticalSort(
            a.name ?? translate.phrases.banyanApp('Unnamed Block'),
            b.name ?? translate.phrases.banyanApp('Unnamed Block'),
          ),
        )

        const items = sortedBlocks.map((block) => {
          const associatedWeatherStationName =
            !!block.associatedWeatherStation && points?.[block.associatedWeatherStation]?.name
              ? points[block.associatedWeatherStation].name
              : translate.phrases.banyanApp('Unknown Weather Station')

          return {
            value: String(block.blockId),
            label: block.name ?? translate.phrases.banyanApp('Unnamed Station'),
            associatedWeatherStationName: showAssociatedWeatherStationName
              ? associatedWeatherStationName
              : undefined,
            associatedWeatherStationExternalVendorNames: block.associatedWeatherStation
              ? points?.[block.associatedWeatherStation]?.externalVendorNames
              : undefined,
          }
        })

        if (!items.length) return []

        return [{ group: propertyName, items }]
      })
  }, [selectedBlock, properties])

  if (!data.length) return null

  return (
    // can prevent the section from collapsing by passing stopPropagation
    <div onClick={(e) => e.stopPropagation()}>
      <Select
        {...selectClickToggle}
        size="xs"
        className={css({
          width: containerWidth < 600 ? 200 : Math.min(280, Math.round(containerWidth / 3)),
          border: `1px solid ${colors.grey500}`,
          color: colors.grey800,
          borderRadius: 3,
        })}
        clearable={false}
        data={data}
        renderOption={({ option }) => {
          const {
            label,
            associatedWeatherStationName,
            associatedWeatherStationExternalVendorNames = [],
          } = option as SelectDataItem

          const weatherStation = (
            <Text size={'xs'} c={colors.grey500}>
              {associatedWeatherStationName}
            </Text>
          )

          return (
            <Group wrap="nowrap" flex={1}>
              <div css={{ flex: 1 }}>
                <Text size={'xs'}>{label}</Text>
                {!!associatedWeatherStationName &&
                  (getExternalVendorIconItems(associatedWeatherStationExternalVendorNames).length ? (
                    <div
                      css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}
                    >
                      {weatherStation}
                      <ExternalVendorIcons
                        externalVendorNames={associatedWeatherStationExternalVendorNames}
                        size={16}
                      />
                    </div>
                  ) : (
                    weatherStation
                  ))}
              </div>
            </Group>
          )
        }}
        filter={mantineSelectSearchFilterProp}
        onChange={(newSelectedBlock) => {
          if (newSelectedBlock === null) return

          setSelectedFieldAsset({ block: Number(newSelectedBlock) })
        }}
        placeholder={translate.phrases.banyanApp('Select a Block')}
        searchable
        classNames={SharedSettings.MANTINE_SELECT_UNDERLINE_SEPARATOR_CLASSNAMES}
        styles={{
          ...SharedSettings.MANTINE_SELECT_RIGHT_ICON_CHANGER,
          ...SharedSettings.MANTINE_SELECT_UNDERLINE_SEPARATOR_STYLES,
          dropdown: { marginTop: -6, fontWeight: 'normal' },
          input: { ...SharedSettings.MANTINE_SELECT_RIGHT_ICON_CHANGER.input, paddingRight: 28 },
        }}
        value={selectedBlock?.toString()}
        comboboxProps={{
          withinPortal: true,
          keepMounted: false,
          zIndex: hubConnectorSettings.zIndexForDropdownSelectorInStackemCharts,
        }}
      />
    </div>
  )
}
